import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    HiOutlineRefresh,
    HiOutlineChevronDown,
    HiOutlineChevronRight,
    HiOutlineInbox
} from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';
import { SendErrorResult, SmtpCheckResult, parseSendError } from './SmtpResult';
import './LogsTab.css';

const EMPTY_FILTERS = { month: '', status: '', batch_id: '', company_name: '' };
const DEBOUNCE_MS = 400;

const LogsTab = () => {
    const [logs, setLogs] = useState([]);
    const [batches, setBatches] = useState([]);
    const [companyNames, setCompanyNames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('emails'); // 'emails' or 'batches'
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Debounced copy of filters — this is what actually triggers fetches,
    // so typing in the Batch ID field doesn't fire a request per keystroke.
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [expandedBatches, setExpandedBatches] = useState(() => new Set());
    const [users, setUsers] = useState([]);
    const [retryUser, setRetryUser] = useState({});     // batch_id -> user id
    const [retrying, setRetrying] = useState(null);     // batch_id being retried
    const [retryResult, setRetryResult] = useState({}); // batch_id -> SmtpCheckResult-shaped object

    useEffect(() => {
        fetchCompanyNames();
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axiosInstance.get('/api/aging-reports/users');
            if (response.data.success) {
                setUsers((response.data.data || []).filter((u) => u.is_active));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    // Debounce: only push filters into appliedFilters after the user pauses.
    useEffect(() => {
        const timer = setTimeout(() => setAppliedFilters(filters), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filters]);

    const fetchEmailLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (appliedFilters.month) params.month = appliedFilters.month;
            if (appliedFilters.status) params.status = appliedFilters.status;
            if (appliedFilters.batch_id) params.batch_id = appliedFilters.batch_id;
            if (appliedFilters.company_name) params.company_name = appliedFilters.company_name;

            const response = await axiosInstance.get('/api/aging-reports/logs/emails', { params });
            if (response.data.success) {
                setLogs(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching email logs:', error);
            alert('Failed to fetch email logs');
        } finally {
            setLoading(false);
        }
    }, [appliedFilters]);

    const fetchBatches = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (appliedFilters.month) params.month = appliedFilters.month;

            const response = await axiosInstance.get('/api/aging-reports/logs/batches', { params });
            if (response.data.success) {
                setBatches(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching batches:', error);
            alert('Failed to fetch batches');
        } finally {
            setLoading(false);
        }
    }, [appliedFilters]);

    useEffect(() => {
        if (activeView === 'emails') {
            fetchEmailLogs();
        } else {
            fetchBatches();
        }
    }, [activeView, fetchEmailLogs, fetchBatches]);

    const fetchCompanyNames = async () => {
        try {
            const response = await axiosInstance.get('/api/aging-reports/company-names');
            if (response.data.success && response.data.data?.company_names) {
                setCompanyNames(response.data.data.company_names);
            }
        } catch (error) {
            console.error('Error fetching company names:', error);
        }
    };

    const handleRefresh = () => {
        if (activeView === 'emails') {
            fetchEmailLogs();
        } else {
            fetchBatches();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    // ---- Email summary (Total / Sent / Failed / Pending) ----
    const summary = useMemo(() => {
        const stats = { total: logs.length, sent: 0, failed: 0, pending: 0 };
        logs.forEach((log) => {
            if (stats[log.status] !== undefined) stats[log.status] += 1;
        });
        const completed = stats.sent + stats.failed;
        stats.successRate = completed > 0 ? Math.round((stats.sent / completed) * 100) : 0;
        return stats;
    }, [logs]);

    // ---- Group email logs by batch, newest batch first ----
    const groupedLogs = useMemo(() => {
        const map = new Map();
        logs.forEach((log) => {
            const key = log.batch_id ?? 'unknown';
            if (!map.has(key)) {
                map.set(key, { batch_id: key, rows: [], sent: 0, failed: 0, pending: 0 });
            }
            const group = map.get(key);
            group.rows.push(log);
            if (group[log.status] !== undefined) group[log.status] += 1;
        });
        return Array.from(map.values()).sort((a, b) => {
            const an = Number(a.batch_id);
            const bn = Number(b.batch_id);
            if (Number.isNaN(an) || Number.isNaN(bn)) return 0;
            return bn - an;
        });
    }, [logs]);

    // Auto-expand groups whenever the result set changes (so filtered/small
    // result sets are immediately visible without an extra click).
    useEffect(() => {
        if (groupedLogs.length > 0 && groupedLogs.length <= 3) {
            setExpandedBatches(new Set(groupedLogs.map((g) => g.batch_id)));
        }
    }, [groupedLogs]);

    // The most common failure reason in a group, so a whole-batch problem
    // (e.g. mailbox login rejected) is explained once above the table
    const topFailure = (group) => {
        const counts = new Map();
        group.rows.forEach((r) => {
            if (r.status === 'failed' && r.error_message) {
                counts.set(r.error_message, (counts.get(r.error_message) || 0) + 1);
            }
        });
        let best = null;
        counts.forEach((n, msg) => { if (!best || n > best.count) best = { message: msg, count: n }; });
        return best;
    };

    const handleRetry = async (batchId) => {
        const userId = retryUser[batchId] || users[0]?.id;
        if (!userId) {
            alert('Add an active email user first (Email Users tab)');
            return;
        }
        const user = users.find((u) => String(u.id) === String(userId));
        if (!window.confirm(`Re-send all failed emails in batch ${batchId} from ${user?.email}?`)) return;

        setRetrying(batchId);
        try {
            const response = await axiosInstance.post(`/api/aging-reports/batches/${batchId}/retry-failed`, { user_id: userId });
            const d = response.data.data || {};
            setRetryResult((prev) => ({
                ...prev,
                [batchId]: {
                    ok: true,
                    title: `${d.queued_for_retry ?? 0} email(s) queued for re-send`,
                    hint: d.missing_pdfs?.length
                        ? `${d.missing_pdfs.length} could not be retried because their PDF is missing - upload the report again for those.`
                        : 'Press Refresh in a minute to see the results.'
                }
            }));
        } catch (error) {
            const check = error.response?.data?.error?.detail?.smtp_check;
            setRetryResult((prev) => ({
                ...prev,
                [batchId]: check || {
                    ok: false,
                    title: error.response?.data?.message || 'Retry failed',
                    hint: ''
                }
            }));
        } finally {
            setRetrying(null);
        }
    };

    const toggleBatch = (batchId) => {
        setExpandedBatches((prev) => {
            const next = new Set(prev);
            if (next.has(batchId)) next.delete(batchId);
            else next.add(batchId);
            return next;
        });
    };

    const allExpanded = groupedLogs.length > 0 && expandedBatches.size === groupedLogs.length;
    const toggleAll = () => {
        if (allExpanded) setExpandedBatches(new Set());
        else setExpandedBatches(new Set(groupedLogs.map((g) => g.batch_id)));
    };

    const hasActiveFilters = filters.month || filters.status || filters.batch_id || filters.company_name;

    return (
        <div className="logs-tab">
            <div className="tab-header">
                <h2>Email Logs &amp; Batches</h2>
                <div className="tab-actions">
                    <div className="view-toggle">
                        <button
                            className={activeView === 'emails' ? 'active' : ''}
                            onClick={() => setActiveView('emails')}
                        >
                            Email Logs
                        </button>
                        <button
                            className={activeView === 'batches' ? 'active' : ''}
                            onClick={() => setActiveView('batches')}
                        >
                            Batches
                        </button>
                    </div>
                    <button onClick={handleRefresh} className="btn-icon" title="Refresh">
                        <HiOutlineRefresh />
                    </button>
                </div>
            </div>

            {/* SUMMARY STATS */}
            {activeView === 'emails' && (
                <div className="stats-row">
                    <div className="stat-card total">
                        <span className="stat-value">{summary.total}</span>
                        <span className="stat-label">Total Emails</span>
                    </div>
                    <div className="stat-card sent">
                        <span className="stat-value">{summary.sent}</span>
                        <span className="stat-label">Sent</span>
                    </div>
                    <div className="stat-card failed">
                        <span className="stat-value">{summary.failed}</span>
                        <span className="stat-label">Failed</span>
                    </div>
                    <div className="stat-card pending">
                        <span className="stat-value">{summary.pending}</span>
                        <span className="stat-label">Pending</span>
                    </div>
                    <div className="stat-card rate">
                        <span className="stat-value">{summary.successRate}%</span>
                        <span className="stat-label">Success Rate</span>
                    </div>
                </div>
            )}

            <div className="filters-section">
                <div className="filter-group">
                    <label>Month (YYYY-MM)</label>
                    <input
                        type="month"
                        value={filters.month}
                        onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                    />
                </div>
                {activeView === 'emails' && (
                    <>
                        <div className="filter-group">
                            <label>Type</label>
                            <select
                                value={filters.company_name}
                                onChange={(e) => setFilters({ ...filters, company_name: e.target.value })}
                            >
                                <option value="">All</option>
                                {companyNames.map((item, index) => (
                                    <option key={index} value={item.value}>
                                        {item.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                <option value="">All</option>
                                <option value="sent">Sent</option>
                                <option value="failed">Failed</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>Batch ID</label>
                            <input
                                type="number"
                                value={filters.batch_id}
                                onChange={(e) => setFilters({ ...filters, batch_id: e.target.value })}
                                placeholder="Filter by batch"
                            />
                        </div>
                    </>
                )}
                <button
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="btn-secondary"
                    disabled={!hasActiveFilters}
                >
                    Clear Filters
                </button>
            </div>

            {loading ? (
                <div className="loading">Loading...</div>
            ) : activeView === 'emails' ? (
                groupedLogs.length === 0 ? (
                    <div className="empty-state">
                        <HiOutlineInbox className="empty-icon" />
                        <p>No email logs found</p>
                        {hasActiveFilters && <span>Try adjusting or clearing your filters.</span>}
                    </div>
                ) : (
                    <div className="batch-groups">
                        <div className="batch-groups-toolbar">
                            <span className="batch-groups-count">
                                {groupedLogs.length} batch{groupedLogs.length !== 1 ? 'es' : ''}
                            </span>
                            <button className="btn-link" onClick={toggleAll}>
                                {allExpanded ? 'Collapse all' : 'Expand all'}
                            </button>
                        </div>
                        {groupedLogs.map((group) => {
                            const isOpen = expandedBatches.has(group.batch_id);
                            const failure = topFailure(group);
                            // Explain once above the table when one reason covers most failures
                            const sharedFailure = failure && failure.count >= Math.max(2, group.failed * 0.5) ? failure : null;
                            return (
                                <div className="batch-group" key={group.batch_id}>
                                    <button
                                        className="batch-group-header"
                                        onClick={() => toggleBatch(group.batch_id)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="batch-group-title">
                                            {isOpen ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />}
                                            Batch {group.batch_id}
                                            <span className="batch-group-total">
                                                {group.rows.length} email{group.rows.length !== 1 ? 's' : ''}
                                            </span>
                                        </span>
                                        <span className="batch-group-stats">
                                            {group.sent > 0 && (
                                                <span className="pill sent">{group.sent} sent</span>
                                            )}
                                            {group.failed > 0 && (
                                                <span className="pill failed">{group.failed} failed</span>
                                            )}
                                            {group.pending > 0 && (
                                                <span className="pill pending">{group.pending} pending</span>
                                            )}
                                        </span>
                                    </button>
                                    {isOpen && group.failed > 0 && (
                                        <div className="batch-failure-banner">
                                            {sharedFailure ? (
                                                <SendErrorResult
                                                    message={sharedFailure.message}
                                                    prefix={sharedFailure.count === group.rows.length
                                                        ? `All ${sharedFailure.count} failed: `
                                                        : `${sharedFailure.count} of ${group.failed} failed: `}
                                                />
                                            ) : <div className="smtp-result-spacer" style={{ flex: 1 }} />}
                                            <div className="batch-retry">
                                                {users.length > 1 && (
                                                    <select
                                                        value={retryUser[group.batch_id] || users[0]?.id || ''}
                                                        onChange={(e) => setRetryUser({ ...retryUser, [group.batch_id]: e.target.value })}
                                                    >
                                                        {users.map((u) => (
                                                            <option key={u.id} value={u.id}>{u.email}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                <button
                                                    className="btn-secondary"
                                                    onClick={() => handleRetry(group.batch_id)}
                                                    disabled={retrying === group.batch_id}
                                                    title="Tests the mailbox first; nothing is sent if login fails"
                                                >
                                                    {retrying === group.batch_id ? 'Checking mailbox…' : `Retry ${group.failed} failed`}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {isOpen && retryResult[group.batch_id] && (
                                        <div className="batch-failure-banner">
                                            <SmtpCheckResult result={retryResult[group.batch_id]} />
                                        </div>
                                    )}
                                    {isOpen && (
                                        <div className="table-container">
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Ledger Code</th>
                                                        <th>Customer</th>
                                                        <th>Email</th>
                                                        <th>Status</th>
                                                        <th>Sent At</th>
                                                        <th>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {group.rows.map((log) => (
                                                        <tr key={log.id}>
                                                            <td>{log.id}</td>
                                                            <td>{log.ledger_code}</td>
                                                            <td>{log.customer_name}</td>
                                                            <td>{log.customer_email || '-'}</td>
                                                            <td>
                                                                <span className={`status-badge ${log.status}`}>
                                                                    {log.status}
                                                                </span>
                                                            </td>
                                                            <td>{formatDate(log.sent_at)}</td>
                                                            <td className="smtp-error-cell">
                                                                {log.status !== 'failed' || !log.error_message
                                                                    ? '-'
                                                                    : sharedFailure && log.error_message === sharedFailure.message
                                                                        ? <span className="error-text">{parseSendError(log.error_message).title}</span>
                                                                        : <SendErrorResult message={log.error_message} />}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Batch ID</th>
                                <th>File Name</th>
                                <th>Customers</th>
                                <th>Sent</th>
                                <th>Failed</th>
                                <th>Pending</th>
                                <th>Status</th>
                                <th>Uploaded At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="no-data">No batches found</td>
                                </tr>
                            ) : (
                                batches.map(batch => (
                                    <tr key={batch.id}>
                                        <td>{batch.id}</td>
                                        <td>{batch.filename}</td>
                                        <td>{batch.total_customers}</td>
                                        <td className="success-text">{batch.sent_count ?? 0}</td>
                                        <td className="error-text">{batch.failed_count ?? 0}</td>
                                        <td>{batch.pending_count ?? 0}</td>
                                        <td>
                                            <span className={`status-badge batch-${batch.status}`}>
                                                {batch.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(batch.uploaded_at)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default LogsTab;
